import { filter, includes, isEmpty, toLower } from 'lodash/fp';

import { Country, LocationResult } from '../../types';
import { PARENTHESIS_MATCH_REGEX } from '../../../../constants';
import extractCityFromParenthesis from '../extract-city-from-parenthesis';
import getCountryMatch from '../get-country-match';
import getMatchedCities from '../get-matched-cities';

const hasParenthesesFormat = (searchTerm: string): boolean => {
  const regex = PARENTHESIS_MATCH_REGEX;
  return regex.test(searchTerm);
};

const PRIORITY_LOCATIONS = ['usa', 'us', 'nz', 'uk'];

const getCountriesBySearch = (countries: Country[], searchTerm: string): LocationResult[] | [] => {
  if (!searchTerm) {
    return [];
  }

  const lowerCaseSearchTerm = toLower(searchTerm);

  // Matches priority locations
  const isPriorityLocation = includes(lowerCaseSearchTerm, PRIORITY_LOCATIONS);
  if (isPriorityLocation) {
    const matchedCities = getMatchedCities({ countries, searchTerm: lowerCaseSearchTerm });
    const filteredCities = filter(
      ({ city }) => !isEmpty(city) && includes(toLower(city?.label), PRIORITY_LOCATIONS),
      matchedCities,
    );

    if (!isEmpty(filteredCities)) {
      return filteredCities;
    }
  }

  const countryMatches = getCountryMatch(countries, lowerCaseSearchTerm);

  // If there are country matches, return them
  if (!isEmpty(countryMatches)) {
    return countryMatches;
  }

  // The search term is in parentheses format eg 'Australia (Sydney)'
  const extractedValues = extractCityFromParenthesis(lowerCaseSearchTerm);
  if (hasParenthesesFormat(lowerCaseSearchTerm) && extractedValues) {
    return getMatchedCities({ countries, countryLabel: extractedValues.country, searchTerm: extractedValues.city });
  }

  // If no matches, search for cities
  return getMatchedCities({ countries, searchTerm: lowerCaseSearchTerm });
};

export default getCountriesBySearch;
